import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, Trash2, Archive } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  updateChannel, inviteMembers, removeMember, deleteChannel, archiveChannel, unarchiveChannel,
  type ChatChannelDetail, type OrgMember,
} from '../../services/chatChannelService';
import MemberSearchInput from './MemberSearchInput';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface Props {
  channel: ChatChannelDetail;
  onClose: () => void;
}

export default function ChannelSettingsModal({ channel, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { openProfileModal } = useProfileModal();
  const isOwner = channel.myRole === 'OWNER';
  const isAdminOrOwner = channel.myRole === 'OWNER' || channel.myRole === 'ADMIN';
  const [name, setName] = useState(channel.name ?? '');
  const [description, setDescription] = useState(channel.description ?? '');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const canArchive = channel.type !== 'DM' && (isOwner || channel.myRole === 'ADMIN');

  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['chatChannel', channel.id] });
    queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateChannel(channel.id, { name: name.trim(), description: description.trim() || undefined });
      invalidate();
      toast.success('채널 설정이 저장되었습니다.');
      onClose();
    } catch {
      toast.error('저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async (member: OrgMember) => {
    try {
      await inviteMembers(channel.id, [member.userId]);
      invalidate();
      toast.success(`${member.name}님을 초대했습니다.`);
    } catch {
      toast.error('초대에 실패했습니다.');
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    try {
      await removeMember(channel.id, userId);
      invalidate();
      toast.success(`${memberName}님을 제거했습니다.`);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '제거에 실패했습니다.';
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteChannel(channel.id);
      queryClient.invalidateQueries({ queryKey: ['chatChannels'] });
      toast.success('채널이 삭제되었습니다.');
      onClose();
      navigate('/chat');
    } catch {
      toast.error('삭제에 실패했습니다.');
    }
  };

  const handleArchive = async () => {
    try {
      await archiveChannel(channel.id);
      invalidate();
      toast.success('채널이 아카이브되었습니다.');
      setConfirmArchive(false);
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '아카이브에 실패했습니다.');
    }
  };

  const handleUnarchive = async () => {
    try {
      await unarchiveChannel(channel.id);
      invalidate();
      toast.success('아카이브가 해제되었습니다.');
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '아카이브 해제에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">채널 설정</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="size-5" /></button>
        </div>

        {isAdminOrOwner ? (
          <div className="space-y-4">
            {/* Name & Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">채널 이름</label>
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={50}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            </div>

            {/* Member management */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">멤버 관리</label>
              <MemberSearchInput
                selectedIds={[]}
                onAdd={handleInvite}
                onRemove={() => {}}
                excludeIds={channel.members.map((m) => m.userId)}
                placeholder="멤버 초대..."
              />
            </div>

            <div className="space-y-1">
              {channel.members.map((m) => (
                <div key={m.userId} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Avatar
                      size="sm"
                      userId={m.userId}
                      name={m.name}
                      avatarUrl={m.avatarUrl}
                      displayColor={m.displayColor as ProfileColorKey | null | undefined}
                      firstName={m.firstName}
                      onClick={() => openProfileModal(m.userId)}
                    />
                    <span className="text-sm text-gray-900">{m.name}</span>
                    {m.role === 'OWNER' && <Crown className="size-3 text-amber-500" />}
                    {m.role === 'ADMIN' && <span className="text-[11px] bg-blue-100 text-blue-700 rounded px-1">관리자</span>}
                  </div>
                  {isOwner && m.role !== 'OWNER' && (
                    <button onClick={() => handleRemoveMember(m.userId, m.name)}
                      className="text-sm text-red-500 hover:text-red-700 px-2 py-1">
                      제거
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Save */}
            <button onClick={handleSave} disabled={saving || !name.trim()}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">
              {saving ? '저장 중...' : '저장'}
            </button>

            {/* Danger zone */}
            {(isOwner || canArchive) && (
              <div className="border-t border-gray-200 pt-4 mt-4 space-y-4">
                <p className="text-sm text-gray-500">위험 영역</p>

                {/* Archive */}
                {canArchive && !channel.isArchived && (
                  confirmArchive ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-700 mb-2">이 채널을 아카이브하시겠습니까? 아카이브된 채널은 읽기 전용이 되며, 새 메시지를 보낼 수 없습니다.</p>
                      <div className="flex gap-2">
                        <button onClick={() => setConfirmArchive(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border rounded-lg">취소</button>
                        <button onClick={handleArchive} className="px-3 py-1.5 text-sm text-white bg-yellow-500 rounded-lg hover:bg-yellow-600">아카이브</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmArchive(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 w-full">
                      <Archive className="size-4" /> 채널 아카이브
                    </button>
                  )
                )}

                {canArchive && channel.isArchived && (
                  <button onClick={handleUnarchive}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 w-full">
                    <Archive className="size-4" /> 아카이브 해제
                  </button>
                )}

                {/* Delete */}
                {isOwner && (confirmDelete ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700 mb-2">이 채널과 모든 메시지가 영구 삭제됩니다.</p>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 text-sm text-gray-600 bg-white border rounded-lg">취소</button>
                      <button onClick={handleDelete} className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700">삭제</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setConfirmDelete(true)}
                    className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 w-full">
                    <Trash2 className="size-4" /> 채널 삭제
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Read-only for MEMBER */
          <div className="space-y-3">
            <div><p className="text-sm text-gray-500">채널 이름</p><p className="text-sm text-gray-900 font-medium">{channel.name}</p></div>
            {channel.description && <div><p className="text-sm text-gray-500">설명</p><p className="text-sm text-gray-700">{channel.description}</p></div>}
            <div><p className="text-sm text-gray-500">멤버 ({channel.memberCount}명)</p>
              <div className="space-y-1 mt-1">
                {channel.members.map((m) => (
                  <div key={m.userId} className="flex items-center gap-2 py-1">
                    <span className="text-sm text-gray-900">{m.name}</span>
                    {m.role === 'OWNER' && <Crown className="size-3 text-amber-500" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
